import * as React from 'react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Outlet } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import { endOfMonth } from 'date-fns'
import { Features } from 'generated/featureDefinitions'
import {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceCustomPeriodEmployeesAnalyticsDocument,
  AttendanceCustomPeriodEmployeesCollectionListDocument,
  AttendanceCustomPeriodEmployeesCollectionListQuery,
  AttendanceStateResolverStateEnum,
  EmployeeCollectionFilterEmployeesNamesDocument,
  TimeInsightsEntitiesClockInStatusStatusEnum,
} from 'generated/resources/graphql'
import { ExportTimeBalanceDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import SendEmailReminderModal from 'modules/attendance/components/SendEmailReminderModal'
import {
  PAYROLL_PERIOD_TYPES,
  type PayrollPeriodTypes,
} from 'modules/attendance/components/TimeTracking/Layout/form'
import RequestModal from 'modules/attendance/components/compensation/payroll/request'
import { CompensationReponse } from 'modules/attendance/components/compensation/payroll/response'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import CustomPeriodEmployeeSidepanel from './CustomPeriodEmployeeSidepanel'
import { downloadAnnualBalanceAction } from './DownloadAnnualBalanceAction'
import { downloadReportAction } from './DownloadReportAction'
import CustomCreateModal from './components/CustomCreateModal'
import CustomResponseModal from './components/CustomResponseModal'
import { useAlerts } from './hooks/useAlerts'
import { useBulkActionsConfig } from './hooks/useBulkActionsConfig'
import { useBulkApprovePeriod } from './hooks/useBulkApprovePeriod'
import { useBulkAttendableUpdate } from './hooks/useBulkAttendableUpdate'
import { useBulkCreatePayrollSupplement } from './hooks/useBulkCreatePayrollSupplement'
import { useColumns } from './hooks/useColumns'
import { managedByEmployeeEnum, useFilters } from './hooks/useFilters'
import { TimeTrackingCollectionEmployee } from './types'

type BulkActionType =
  | 'approve'
  | 'disapprove'
  | 'send-reminder'
  | 'compensate'
  | 'exclude'
  | 'include'
const isDateNavigationToday = ({ dateFrom, dateTo }: { dateFrom: Date; dateTo: Date }) => {
  const today = i18nDateToString(new Date(), 'backend')

  return [dateFrom, dateTo].every((date) => i18nDateToString(date, 'backend') === today)
}

const TimeTrackingCollection = () => {
  const { company, employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()
  const { dateFrom, dateTo, cycle, onDateChange } = useAttendanceDateNavigator()
  const { alertsFilter, hasTimeTrackingInconsistencies } = useAlerts()
  const { columns } = useColumns()
  const [customPeriodEmployee, setCustomPeriodEmployee] =
    React.useState<TimeTrackingCollectionEmployee | null>(null)

  const { filters: employeeFilters } = useEmployeeFilters()

  const [timeTrackingState, setTimeTrackingState] = React.useState<{
    showCustomPeriodEmployeeSidepanel: boolean
    payrollCompensationModalOpen: boolean
    payrollCompensationRequestModalOpen: boolean
    payrollCompensationResponseModalOpen: boolean
    selectedEmployeeIds: string[]
    selectedEmployees: AttendanceCustomPeriodEmployeeItemFragment[]
    selectedPayrollPeriod: PayrollPeriodTypes
    response: CompensationReponse
    emailReminderModalOpen: boolean
  }>({
    showCustomPeriodEmployeeSidepanel: false,
    payrollCompensationModalOpen: false,
    payrollCompensationRequestModalOpen: false,
    payrollCompensationResponseModalOpen: false,
    selectedEmployeeIds: [],
    selectedEmployees: [],
    selectedPayrollPeriod: PAYROLL_PERIOD_TYPES.current,
    response: { successes: [], errors: [] },
    emailReminderModalOpen: false,
  })

  const toggleTimeTrackingState = React.useCallback(
    <K extends keyof typeof timeTrackingState>(key: K, value: (typeof timeTrackingState)[K]) => {
      setTimeTrackingState((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const { featureAvailability: timeBalanceEnabled } = useAttendanceFeatures(
    Features.time_balance_annualisation_export
  )

  const { data: exportTimeBalanceData } = useQuery(ExportTimeBalanceDocument, {})
  const canAccessTimeBalanceExport =
    !!exportTimeBalanceData?.timeBalance?._policies.accessSeeTimeBalanceExport.can
  const showExportYearlyBalanceOption = timeBalanceEnabled && canAccessTimeBalanceExport

  const { bulkApprovePeriod, bulkDisapprovePeriod } = useBulkApprovePeriod({
    cycle,
    startOn: i18nDateToString(dateFrom, 'backend'),
    endOn: i18nDateToString(dateTo, 'backend'),
  })

  const periodType = params.periodType || 'daily'

  const { getBulkActions } = useBulkActionsConfig({
    isDateNavigationToday: isDateNavigationToday({ dateFrom, dateTo }),
  })
  const { bulkUpdateAttendable } = useBulkAttendableUpdate()
  const { bulkCompensatePeriods } = useBulkCreatePayrollSupplement()

  const client = useApolloClient()
  const currentEmployeeId = currentEmployee?.id
  const { analyticsFilters, updateFilters } = useFilters({
    currentEmployeeId,
    hasTimeTrackingInconsistencies,
  })

  const trackingData = React.useMemo(
    () => ({
      period_type: cycle,
      start_on: i18nDateToString(dateFrom, 'backend'),
      end_on: i18nDateToString(dateTo, 'backend'),
    }),
    [cycle, dateFrom, dateTo]
  )

  const openSidepanel = (item: TimeTrackingCollectionEmployee, trackingEvent: string) => {
    track(trackingEvent, trackingData)
    setCustomPeriodEmployee(item)
    toggleTimeTrackingState('showCustomPeriodEmployeeSidepanel', true)
  }

  const { data: analyticsData } = useQuery(AttendanceCustomPeriodEmployeesAnalyticsDocument, {
    variables: {
      companyId: company.id,
      endOn: i18nDateToString(dateTo, 'backend'),
      startOn: i18nDateToString(dateFrom, 'backend'),
      ...analyticsFilters,
    },
  })

  const defaultGranularity = React.useMemo<'week' | 'month' | 'day'>(() => {
    if (cycle === 'weekly') return 'week'
    if (cycle === 'monthly') return 'month'
    return 'day'
  }, [cycle])

  const reviewStatusResult =
    analyticsData?.attendance?.customPeriodEmployeesConnection?.aggregate?.terms?.reviewStatus

  const countItemsForReviewStatus = useCallback(
    (reviewStatusSelected: AttendanceStateResolverStateEnum) => {
      return () => {
        if (!reviewStatusResult) return 0

        const index = reviewStatusResult?.values.findIndex(
          (status) => status === reviewStatusSelected
        )

        return index !== undefined ? reviewStatusResult.count?.[index] ?? 0 : 0
      }
    },
    [reviewStatusResult]
  )

  const source = useGraphqlDataCollectionSource(
    {
      document: AttendanceCustomPeriodEmployeesCollectionListDocument,
      mapVariables: ({ filters, sortings, search, navigationFilters }) => {
        const dateFromActive = navigationFilters?.date?.value?.from || dateFrom
        const dateToActive = navigationFilters?.date?.value?.to || dateTo
        const today = new Date()
        const effectiveEndDate =
          dateToActive && dateToActive.getTime() > today.getTime() ? today : dateToActive ?? today
        let cycleGranularity: 'weekly' | 'monthly' | 'daily' = 'daily'
        if (navigationFilters?.date?.granularity === 'week') {
          cycleGranularity = 'weekly'
        } else if (navigationFilters?.date?.granularity === 'month') {
          cycleGranularity = 'monthly'
        }

        if (dateFromActive && dateToActive) {
          onDateChange([dateFromActive, dateToActive])
          window.history.pushState(
            null,
            '',
            navigation.timeTracking.list.periodTypeYearMonthDay.fullPath({
              periodType: cycleGranularity,
              year: Number(dateFromActive.getFullYear()),
              month: Number(dateFromActive.getMonth() + 1),
              day: Number(dateFromActive.getDate()),
            })
          )
        }

        const {
          onlyMyReportees,
          reportingTo,
          timeInsightsInconsistencyTypes,
          onlyDirectReports,
          timeoffManagerIds,
          onlyActive,
          teamIds,
          locationIds,
          reviewStatus,
          onlyNonAttendable,
        } = updateFilters(filters)

        const isViewingToday = isDateNavigationToday({
          dateFrom: dateFromActive,
          dateTo: dateToActive,
        })

        return {
          companyId: company.id,
          onlyMyReportees,
          reportingTo,
          timeInsightsInconsistencyTypes,
          onlyDirectReports,
          timeoffManagerIds,
          onlyActive,
          sort: sortings?.[0],
          search: search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          reviewStatus,
          hasTimeTrackingInconsistencies,
          includeClockInStatuses: isViewingToday,
          startOn: i18nDateToString(dateFromActive, 'backend'),
          endOn: i18nDateToString(effectiveEndDate, 'backend'),
          onlyNonAttendable,
        }
      },
      mapPagination: (data) => {
        return {
          total: data.attendance.customPeriodEmployeesConnection.totalCount ?? 0,
        }
      },
      mapData: (
        data: AttendanceCustomPeriodEmployeesCollectionListQuery
      ): TimeTrackingCollectionEmployee[] => {
        const employees = data.attendance.customPeriodEmployeesConnection.edges.map(
          (edge) => edge.node
        )
        const clockInStatuses = data.timeInsights?.clockInStatusesConnection?.nodes || []

        const clockInStatusMap = new Map<string, TimeInsightsEntitiesClockInStatusStatusEnum>()
        clockInStatuses.forEach((status) => {
          if (status.employee?.id && status.status) {
            clockInStatusMap.set(status.employee.id.toString(), status.status)
          }
        })

        const employeesWithStatus = employees.map((employee) => ({
          ...employee,
          clockInStatus: clockInStatusMap.get(employee.id?.toString() || '') ?? undefined,
          isDaily: employee.referenceContract?.frForfaitJours,
        }))

        return employeesWithStatus
      },
      itemOnClick: (item) => {
        return () => {
          openSidepanel(item, trackingEvents.timeTracking.openTTCollectionSidepanelFromRow)
        }
      },
      itemActions: (item) => [
        {
          label: i18n.t('time_tracking.table_collection.employee_summary.title'),
          icon: EyeVisible,
          type: 'primary',
          onClick: () => {
            openSidepanel(
              item,
              trackingEvents.timeTracking.openTTCollectionSidepanelFromDetailsButton
            )
          },
        },
      ],
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        alertsCount: {
          label: i18n.t('time_tracking.table_collection.alerts'),
        },
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      navigationFilters: {
        date: {
          type: 'date-navigator',
          defaultValue: dateFrom,
          defaultFrom: dateFrom,
          defaultTo: dateTo,
          defaultGranularity: defaultGranularity,
          granularity: ['week', 'month', 'day'],
          max: endOfMonth(new Date()),
        },
      },
      filters: {
        reviewStatus: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.review_status'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_pending'),
                value: 'pending',
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_approved'),
                value: 'approved',
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.review_status_in_progress'),
                value: 'in_progress',
              },
            ],
          },
        },
        status: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.excluded_employees'),
                value: 'only_non_attendable',
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.only_active'),
                value: 'only_active',
              },
            ],
          },
        },
        team: employeeFilters.team,
        workplace: employeeFilters.workplace,
        directReports: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.managed_by_me'),
          options: {
            options: [
              {
                label: i18n.t('time_tracking.table_collection.filters.managed_by_me_option'),
                value: managedByEmployeeEnum.managed_by_me,
              },
              {
                label: i18n.t('time_tracking.table_collection.filters.managed_by_others'),
                value: managedByEmployeeEnum.managed_by_others,
              },
              {
                label: i18n.t(
                  'time_tracking.table_collection.filters.managed_directly_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_directly_by_me,
              },
              {
                label: i18n.t(
                  'time_tracking.table_collection.filters.managed_timeoff_by_me_option'
                ),
                value: managedByEmployeeEnum.managed_timeoff_by_me,
              },
            ],
          },
        },
        reportingTo: {
          type: 'in',
          label: i18n.t('time_tracking.table_collection.filters.reporting_to'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterEmployeesNamesDocument,
                variables: {
                  companyId: company.id,
                },
                fetchPolicy: 'cache-first',
              })
              return data.employees.employeeNames.map((employee) => ({
                label: employee.fullName,
                value: employee.id,
                avatar: {
                  type: 'person',
                  firstName: employee.preferredName || employee.firstName,
                  lastName: employee.lastName,
                  src: employee.avatar?.url || undefined,
                },
              }))
            },
          },
        },
        ...alertsFilter,
      },
      presets: [
        {
          label: i18n.t('time_tracking.table_collection.filters.review_status_pending'),
          filter: {
            reviewStatus: ['pending'],
          },
          itemsCount: countItemsForReviewStatus('pending'),
        },
        {
          label: i18n.t('time_tracking.table_collection.filters.review_status_approved'),
          filter: {
            reviewStatus: ['approved'],
          },
          itemsCount: countItemsForReviewStatus('approved'),
        },
      ],
      selectable: (item) => item.id ?? undefined,
      totalItemSummary: (totalCount) =>
        i18n.tp('time_tracking.table_collection.count_employees', { count: totalCount }),
      secondaryActions: () =>
        compact([
          downloadReportAction({
            params,
            navigate,
            navigation,
          }),
          downloadAnnualBalanceAction({
            params,
            navigate,
            navigation,
            showExportYearlyBalanceOption,
          }),
        ]),
      bulkActions: (bulkActionParams) =>
        getBulkActions({ itemsStatus: bulkActionParams.itemsStatus, periodType }),
    },
    [company.id]
  )

  const onCompensatePeriods = async () => {
    const payrollPeriodType = timeTrackingState.selectedPayrollPeriod
    toggleTimeTrackingState('payrollCompensationModalOpen', false)
    toggleTimeTrackingState('payrollCompensationRequestModalOpen', true)

    try {
      const result = await bulkCompensatePeriods({
        employeeIds: timeTrackingState.selectedEmployeeIds.map((id) => parseInt(id, 10)),
        startOn: dateFrom,
        endOn: dateTo,
        period: payrollPeriodType,
      })

      if (result?.manualCompensation) {
        toggleTimeTrackingState('response', {
          successes: (result.manualCompensation.successes || []).map((item) => ({
            employee_id: item.employeeId || 0,
            error: item.error || '',
          })),
          errors: (result.manualCompensation.errors || []).map((item) => ({
            employee_id: item.employeeId || 0,
            error: item.error || '',
          })),
        })
      } else {
        toggleTimeTrackingState('response', { successes: [], errors: [] })
      }

      toggleTimeTrackingState('payrollCompensationRequestModalOpen', false)
      toggleTimeTrackingState('payrollCompensationResponseModalOpen', true)
    } catch (error) {
      toggleTimeTrackingState('response', { successes: [], errors: [] })
      toggleTimeTrackingState('payrollCompensationRequestModalOpen', false)
      toggleTimeTrackingState('payrollCompensationResponseModalOpen', true)
    }
  }

  const onCloseCompensationModal = () => {
    toggleTimeTrackingState('payrollCompensationResponseModalOpen', false)
    toggleTimeTrackingState('selectedEmployeeIds', [])
    toggleTimeTrackingState('selectedEmployees', [])
  }

  const handleBulkAction = async (
    action: BulkActionType | string,
    selectedItems: {
      itemsStatus: readonly { checked: boolean; item: AttendanceCustomPeriodEmployeeItemFragment }[]
    }
  ) => {
    const selectedEmployeeItems = selectedItems.itemsStatus
      .filter(
        (selectedItem: { checked: boolean; item: AttendanceCustomPeriodEmployeeItemFragment }) =>
          selectedItem.checked === true
      )
      .map(
        (selectedItem: { item: AttendanceCustomPeriodEmployeeItemFragment }) => selectedItem.item
      )

    const employeeIds = selectedEmployeeItems
      .map((item) => item.id?.toString())
      .filter((id): id is string => id !== undefined)

    // Update selected employees state for compensation modal
    toggleTimeTrackingState('selectedEmployees', selectedEmployeeItems)

    switch (action) {
      case 'approve':
        await bulkApprovePeriod(employeeIds)
        break
      case 'disapprove':
        await bulkDisapprovePeriod(employeeIds)
        break
      case 'send-reminder':
        toggleTimeTrackingState('selectedEmployeeIds', employeeIds)
        toggleTimeTrackingState('emailReminderModalOpen', true)
        break
      case 'compensate':
        toggleTimeTrackingState('selectedEmployeeIds', employeeIds)
        toggleTimeTrackingState('payrollCompensationModalOpen', true)
        break
      case 'exclude':
        await bulkUpdateAttendable(
          employeeIds.map((id) => ({
            employeeId: Number(id),
            attendable: false,
          }))
        )
        break
      case 'include':
        await bulkUpdateAttendable(
          employeeIds.map((id) => ({
            employeeId: Number(id),
            attendable: true,
          }))
        )
        break
      default:
        break
    }
  }

  return (
    <>
      {timeTrackingState.showCustomPeriodEmployeeSidepanel && (
        <CustomPeriodEmployeeSidepanel
          employee={customPeriodEmployee}
          onClose={() => {
            toggleTimeTrackingState('showCustomPeriodEmployeeSidepanel', false)
            setCustomPeriodEmployee(null)
          }}
          onEmployeeUpdate={(updatedEmployee) => {
            setCustomPeriodEmployee(updatedEmployee)
          }}
        />
      )}
      <ResponsivePage.Body>
        <OneDataCollection
          source={source}
          onBulkAction={handleBulkAction}
          visualizations={[
            {
              type: 'table',
              options: {
                columns,
              },
            },
          ]}
          emptyStates={{
            'no-results': {
              title: i18n.t('time_tracking.table_collection.empty_state.title'),
              description: i18n.t('time_tracking.table_collection.empty_state.description'),
              emoji: '🧐',
            },
          }}
        />
      </ResponsivePage.Body>
      <Outlet />
      {timeTrackingState.emailReminderModalOpen && (
        <SendEmailReminderModal
          employeeId={0}
          employeeFullName=""
          selectedEmployeeIds={timeTrackingState.selectedEmployeeIds.map((id) => Number(id))}
          onClose={() => {
            toggleTimeTrackingState('emailReminderModalOpen', false)
            toggleTimeTrackingState('selectedEmployeeIds', [])
          }}
        />
      )}
      {timeTrackingState.payrollCompensationModalOpen && (
        <CustomCreateModal
          onSubmit={onCompensatePeriods}
          onCloseModal={() => toggleTimeTrackingState('payrollCompensationModalOpen', false)}
          selected={timeTrackingState.selectedEmployees}
          selectedPayrollPeriod={timeTrackingState.selectedPayrollPeriod}
          onPayrollPeriodChange={(period: 'current' | 'next') =>
            toggleTimeTrackingState('selectedPayrollPeriod', period)
          }
        />
      )}
      {timeTrackingState.payrollCompensationRequestModalOpen && <RequestModal />}
      {timeTrackingState.payrollCompensationResponseModalOpen && (
        <CustomResponseModal
          response={timeTrackingState.response}
          selected={timeTrackingState.selectedEmployees}
          onCloseModal={onCloseCompensationModal}
        />
      )}
    </>
  )
}

export default TimeTrackingCollection
